import type { AcceptDecision } from './accept-rules';
import type { ClinicalRole } from '../../auth/use-role';

/**
 * The clinician-facing VERB for an accept decision. Pure — no network, no React, no side effects.
 *
 * WHY THIS EXISTS. Ticking a proposal does five materially different things — `decideAccept`
 * already names all five — and two of them WRITE TO THE CHART. Today every one of them is driven
 * by the same unlabelled 16px checkbox, so the control cannot tell a clinician whether her click
 * is about to open a picker or record a diagnosis. She finds out afterwards, in the A/P. That is
 * not a polish issue; it is a control that silently changes the record.
 *
 * The fix is to compute the label from the decision instead of hard-coding one affordance. Which
 * means the label has to be derivable BEFORE the click, from the same inputs the decision was —
 * hence a pure function over `AcceptDecision`, not a string chosen inside a card component.
 *
 * SCOPE. This module decides what the control SAYS, and whether there is a control at all. It is
 * NOT the authorization. The servers are (Scrips.Patient's order/stop gate, Master's
 * `NonPrescriberActionFilter`); in the UI, `deriveAllowedOrderTypes` in `auth/use-role.ts` applies
 * the physician allowlist and then the `Manage_Orders_Prescriptions` permission (per-type keys
 * returned [] in the PROD-1297 live smoke and disabled every order button). Nothing here may be
 * relied on to prevent a write; the host still applies its own gate.
 * What this does is stop the UI OFFERING an action the host will refuse.
 */

/**
 * Verbs, per decision. Samer approved these five strings; they are not paraphrasable.
 *
 * THE TRAILING ELLIPSIS IS LOAD-BEARING, not typographic decoration. A bare verb means the click
 * COMPLETES the action — "Add order" and the record has an order. An ellipsis means ANOTHER STEP
 * FOLLOWS: a picker, a search, a dose form. That distinction is the entire remedy for the
 * unlabelled checkbox, because it is what lets a clinician predict whether she is about to commit
 * something or merely open something. Drop the ellipsis from "Choose code…" and the control is
 * back to lying about its own consequence.
 *
 * The character is U+2026, matching the convention already in use across the app ("Saving…",
 * "Searching…", "Select a patient…", "Reading Emirates ID…") — never three periods, which look
 * identical in a screenshot but are read out as three separate stops by a screen reader.
 *
 * "ACCEPT" IS BANNED, in any casing. "Accept" is OUR vocabulary: a `Proposal` moving from
 * `pending` to `accepted` in `ambient-session-reducer`. The clinician is not accepting anything —
 * she is adding a diagnosis, or choosing a code. Putting the internal state machine on the button
 * makes the UI something that has to be taught rather than read, and it tells her nothing about
 * what the chart will look like afterwards. The test asserts the ban as a property, not as a
 * spot-check, so it survives a well-meant copy edit.
 */
const VERBS: Readonly<Record<AcceptDecision['action'], string>> = {
  /**
   * THE SIXTH STRING, AND THE ONLY ONE SAMER HAS NOT SIGNED OFF (PROD-2018) — flagged in the PR.
   *
   * `decideAccept` refuses to produce a writing decision while a dictated schedule contradicts the
   * structured one, so there is no write to label here. The question this module then has to answer
   * is the one its own header asks: what does the control SAY, and is there a control at all?
   *
   * A VERB, NOT `null`, AND THE CHOICE IS DELIBERATE. `null` is this module's word for "the action
   * was never hers on this encounter" — a signed note, a nurse on a prescription — and it renders
   * NOTHING precisely because there is nothing she could do about it. That is the wrong sentence
   * here. A contested schedule is not withheld FROM the clinician; it is waiting ON her, and there
   * is a specific thing for her to do. Returning `null` would say "no action here" about the one
   * card in the panel that most needs one, and it would collapse two different facts into a single
   * signal — the medication card keys its resolution controls off `Boolean(acceptVerb)`, so a
   * `null` would also remove the two buttons that ARE the way out, leaving a patient-safety warning
   * with no answer. `every-state-has-an-exit.test.tsx` is the test that catches that.
   *
   * The ellipsis is mandatory and honest: answering the contest is a step, and the catalogue still
   * follows it.
   *
   * WHAT RENDERS IT. Not the accept button — there is no accept to make. The medication card
   * renders this as the PROMPT on the contest's own two actions ("Use what was dictated" / "Keep
   * the schedule as written"), the same shape the decline row already uses for "Why?". The card
   * therefore offers exactly one decision at a time, and never a control the decision layer would
   * refuse to honour.
   */
  'resolve-schedule-contest': 'Confirm schedule…',

  /**
   * A direct write. Reached only when a model-proposed code AND Master's canonical name both
   * agree — see the four-condition guard in `decideAccept`. "A&P" is the name clinicians already
   * use for the destination in this app, so the verb states where the diagnosis lands, not what
   * the system did with the proposal.
   */
  'add-diagnosis': 'Add to A&P',

  /**
   * THE DIAGNOSIS DEFAULT, and the reason this is a default rather than an exception: QA found
   * confirmed-disease codes assigned to unconfirmed conditions in three of four scripts. Both
   * Master paths GENERATE ICD codes from a prompt rather than retrieve them from terminology, so
   * a dictated diagnosis carries a lookup hint and nothing more.
   *
   * So a dictated diagnosis routes to the picker unless everything lines up. "Choose code" says
   * the honest thing — the condition was heard, the code is the clinician's to pick.
   */
  'seed-diagnosis-search': 'Choose code…',

  /**
   * RULE 5 (dev interim A6): a diagnosis the consultation did not confirm. The click appends one
   * line to the A&P assessment text and that is the whole action, so the verb is BARE — no search
   * or form follows, and an ellipsis would promise a step that does not exist. It names where the
   * line lands ("assessment"), not the diagnosis block it deliberately stays out of.
   *
   * Physician-only like every other accept verb: the withholding rules below apply unchanged.
   */
  'append-assessment': 'Add to assessment',

  /**
   * NOT a direct write, despite the decision's name. The host dispatches `add-order` and
   * `open-order-dialog` through the SAME branch — both open the A/P per-type dialog — and
   * `add-order` differs only in that `decideAccept` has already established there is exactly one
   * diagnosis for the dialog to link to. The clinician still has a form to complete.
   *
   * So the ellipsis is mandatory. "Add order" as a bare verb claimed the click COMPLETED the
   * order, which is the precise lie this module exists to stop — and it was doing it on the one
   * branch where the promise was easiest to believe.
   */
  'add-order': 'Add order…',

  /**
   * The same dialog, reached when there is no single diagnosis to link to. It reads identically
   * because the CONSEQUENCE is identical: a form opens. A label may only promise what the click
   * does, and these two clicks do the same thing; the difference between them (pre-linked versus
   * pick-a-diagnosis) is visible inside the dialog, which is where it is actionable.
   *
   * It previously read "Set dose…", which was wrong on every proposal that can reach it.
   * Medications and vaccinations are diverted to the catalogue by `SEARCH_FIRST` BEFORE either
   * order branch, so the only kinds that arrive here are labs, imaging, procedures and referrals
   * — none of which has a dose. The verb named a field the form does not contain.
   */
  'open-order-dialog': 'Add order…',

  /**
   * The order search, pre-filled (PROD-1977). "Catalogue" is deliberate: the clinician is being
   * sent to pick a real coded record, and calling it a search would suggest the system failed to
   * find something. It did not fail — ambient supplies medications and vaccinations with NO CODE,
   * so the catalogue is the only path that ends in a valid order.
   */
  'seed-order-search': 'Find in catalogue…',
};

/**
 * ONLY PHYSICIANS ORDER (PROD-2669, Samer 2026-09-25; administrators confirmed by Andrew).
 *
 * Every order verb — medication, vaccination, lab, imaging, procedure, referral, and the schedule
 * contest that precedes a medication order — is offered to a PHYSICIAN only.
 *
 * AND ONLY PHYSICIANS ACCEPT A DIAGNOSIS (PROD-2691, Samer 2026-09-28). Diagnosis is clinical
 * judgement, so "Choose code…" and "Add to A&P" are withheld from a nurse, an allied-health
 * clinician and every other non-physician too. They used to be exempt as "documentation"; the
 * ruling reversed that. Scrips.Patient now refuses the diagnosis accept for anyone outside the
 * allowlist (403 `diagnosis-accept-not-permitted`), so offering the verb would only lead to a 403.
 * Decline and "View source" are not accept verbs and stay on the card for everyone.
 *
 * This replaces two narrower rules: nurses used to lose only medication and vaccination (PROD-2603
 * left that "pending a product decision"; the decision is now made), and allied health lost
 * medication, vaccination, lab and imaging. The server now refuses every one of those for every
 * non-physician (Scrips.Patient's gate and Master's `NonPrescriberActionFilter`, driven by the
 * Scrips.Common allowlist), so offering any of them would only lead to a 403.
 */

/**
 * Who is looking, for the purpose of which verbs are offered.
 *
 * `'doctor'` is a PHYSICIAN (`mayOrder`, the {3, 8, 18} allowlist in `auth/roles.ts`) and is the
 * ONLY role that gets order verbs. `'nurse'` / `'allied-health'` keep their names because other
 * surfaces tell them apart; `'non-physician'` is every other persona (administrator, receptionist,
 * phlebotomist, technician, an unknown or blank persona).
 */
export type ClinicianRole = 'doctor' | 'nurse' | 'allied-health' | 'non-physician';

/**
 * The ambient panel's role, from `useRole().clinicalRole` and `useRole().mayOrder` (PROD-2669).
 *
 * `mayOrder` is the ONLY way to `'doctor'`. A clinical role of `'physician'` is not enough on its
 * own: `clinicalRoleFor` falls back to `'physician'` for any account it has no persona for.
 */
export function clinicianRoleFor(clinicalRole: ClinicalRole, mayOrder: boolean): ClinicianRole {
  if (clinicalRole === 'nurse') return 'nurse';
  if (clinicalRole === 'allied-health') return 'allied-health';
  return mayOrder ? 'doctor' : 'non-physician';
}

export interface VerbContext {
  role: ClinicianRole;
  /**
   * True once the encounter is signed and locked. Nothing may be written to it, so no proposal
   * carries an action.
   */
  encounterLocked: boolean;
}

/**
 * The verb to render, or `null` for NO CONTROL AT ALL.
 *
 * Null rather than a disabled button, and the difference matters. A greyed-out "Find in
 * catalogue…" reads as a failure the clinician caused: she tries it, nothing happens, and there is
 * no explanation anywhere on the card. Rendering nothing says the truthful thing — this finding is
 * here to be read, and the action was never hers on this encounter. The card keeps its content;
 * it just does not pretend to offer a write.
 *
 * Two withholding rules, in this order:
 *
 *   1. A LOCKED ENCOUNTER removes every verb, for every role. The record is closed; a control
 *      that would amend it should not exist. This is first because it is unconditional — no role
 *      overrides a signed encounter.
 *   2. ONLY A PHYSICIAN ORDERS (PROD-2669) OR ACCEPTS A DIAGNOSIS (PROD-2691). Every role other
 *      than `'doctor'` loses every accept verb, diagnosis included. Decline is not a verb here and
 *      is unaffected.
 *
 * Note that rules 1 and 2 cover combinations `decideAccept` cannot currently produce — an
 * `add-order` or `open-order-dialog` for a medication, which `SEARCH_FIRST` diverts to the
 * catalogue before either branch is reached. They are handled here regardless, because the day
 * medications arrive with a code and that diversion is lifted, the nurse gate must already be in
 * the right place rather than needing to be remembered.
 *
 * BOTH RULES RUN BEFORE `resolve-schedule-contest` IS LABELLED, and that ordering is the correct
 * one (PROD-2018). A signed encounter and a nurse's prescription still yield `null`, so a contested
 * medication on either shows the disagreement and offers no resolution control — there is nothing a
 * resolution could change for a clinician who cannot order the drug on this encounter. The pair
 * stays on screen; only the actions go, which is exactly the line rule 2 already draws.
 */
export function acceptVerb(decision: AcceptDecision, ctx: VerbContext): string | null {
  if (ctx.encounterLocked) return null;

  // PROD-2669 + PROD-2691: no exemption for the diagnosis verbs any more.
  if (ctx.role !== 'doctor') return null;

  return VERBS[decision.action];
}
